// @ts-strict-ignore
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleUsageVO } from '@article/model/vo/article.vo';
import { Euler, Vector3 } from 'three';
import { Dictionary } from 'typescript-collections';
import { CompositionContainer } from '@3d-scene/model3d/composition-container';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ProjectModel } from '@project/model/project.model';
import { ArticleUtils } from '@article/util/article-utils';
import { ConnectionAreaVO, ConnectionVO, MetaElementUsageIdentityVO, TextureAreaUsageVO } from '../../vo/meta-element.vo';
import { LabelingTextureData } from '@project/components/label/labeling-texture-data';
import { ArticleContainerFactory } from '@3d-scene/model3d/article-container.factory';
import { ProjectArticleService } from '@project/service/project-article.service';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ConnectionAreaUsageUtils } from '../../util/connection-area-usage-utils';

export class BaseArticleOperation {

  protected _articleUsage: ArticleUsageVO;
  protected _articleContainer: ArticleContainer;
  protected _compositionId: string;
  private _acceptingMetaElementToGivingIdsMap: Dictionary<string, MetaElementUsageIdentityVO[]> = new Dictionary<
    string,
    MetaElementUsageIdentityVO[]
  >();
  private _textureMetaElementToMergedIdsMap: Dictionary<string, LabelingTextureData> = new Dictionary<string, LabelingTextureData>();

  private _position: Vector3;
  private _rotation: Euler;

  constructor(
    protected projectModel: ProjectModel,
    private articleContainerFactory: ArticleContainerFactory,
    private containerService: ContainerService,
    articleContainer?: ArticleContainer
  ) {
    if (articleContainer) {
      this.fillAcceptingConnectionsMap(articleContainer);
      this.fillMergedTextureConnectionsMap(articleContainer.articleUsage);
      const composition: CompositionContainer = containerService.getCompositionContainer(articleContainer);
      this._compositionId = composition ? composition.compositionGuid : null;
      this._articleContainer = articleContainer;
      this._articleUsage = articleContainer.articleUsage;
      this._position = articleContainer.position.clone();
      this._rotation = articleContainer.rotation.clone();
    }
    if (this.projectModel.isAdminView) {
      this._articleUsage = this.projectModel.getCurrentLoadedArticleUsage();
    }
  }

  public get articleUsageId(): string {
    return this._articleUsage.id;
  }

  public get articleUsage(): ArticleUsageVO {
    return this._articleUsage;
  }

  public async attachArticleContainer(
    projectArticleService: ProjectArticleService,
    projectMetaElementService: ProjectMetaElementService,
    forceArticleRefresh: boolean = true,
    forceProjectRefresh: boolean = true
  ): Promise<ArticleContainer> {
    const compositionContainer: CompositionContainer = this.containerService.findCompositionContainerInGlobal(this._compositionId);
    if (!compositionContainer) {
      return null;
    }
    const articleContainer: ArticleContainer = this.createArticleContainer(projectArticleService, projectMetaElementService);
    await compositionContainer.attachArticleContainer(articleContainer, forceArticleRefresh, forceProjectRefresh);
    return articleContainer;
  }

  private createArticleContainer(projectArticleService: ProjectArticleService, projectMetaElementService: ProjectMetaElementService): ArticleContainer {
    projectArticleService.storeArticleUsage(this._articleUsage);
    this.restoreGivingMetaUsages(this._articleUsage, projectMetaElementService);
    this.restoreAcceptingMetaUsages(this._articleUsage);
    this.restoreMergedTextureData(this._articleUsage, projectMetaElementService);
    const newContainer: ArticleContainer = this.articleContainerFactory.getNewInstance();
    newContainer.articleGUID = this._articleUsage.id;
    newContainer.rotateByEuler(this._rotation);
    newContainer.moveTo(this._position.x, this._position.y, this._position.z);
    return newContainer;
  }

  private fillAcceptingConnectionsMap(container: ArticleContainer): void {
    for (const connectionArea of ArticleUtils.getConnectionElements(container.article, ConnectionAreaVO.SIDE_ACCEPTING)) {
      const connectionAreaUsage = ArticleUsageUtils.findConnectionAreaUsageByName(container.articleUsage, connectionArea.name);
      if (connectionAreaUsage) {
        const givingAreaIds: MetaElementUsageIdentityVO[] = connectionAreaUsage.connection.givingAreaIds;
        if (givingAreaIds && givingAreaIds.length > 0) {
          this._acceptingMetaElementToGivingIdsMap.setValue(connectionArea.name, givingAreaIds.concat());
        }
      }
    }
  }

  private fillMergedTextureConnectionsMap(articleUsage: ArticleUsageVO): void {
    if (!articleUsage || !articleUsage.metaElementUsages) {
      return;
    }
    for (const textureAreaUsage of ArticleUsageUtils.textureAreaUsages(articleUsage)) {
      if (textureAreaUsage.previousMergedUsageId || textureAreaUsage.nextMergedUsageId) {
        const labelingData: LabelingTextureData = new LabelingTextureData();
        labelingData.previousMetaUsageId = textureAreaUsage.previousMergedUsageId;
        labelingData.nextMetaUsageId = textureAreaUsage.nextMergedUsageId;
        this._textureMetaElementToMergedIdsMap.setValue(textureAreaUsage.identity.metaElementName, labelingData);
      }
    }
  }

  private restoreMergedTextureData(articleUsage: ArticleUsageVO, projectMetaElementService: ProjectMetaElementService): void {
    if (!articleUsage || !articleUsage.metaElementUsages) {
      return;
    }
    for (const textAreaUsage of ArticleUsageUtils.textureAreaUsages(articleUsage)) {
      if (this._textureMetaElementToMergedIdsMap.containsKey(textAreaUsage.identity.metaElementName)) {
        const labelingData: LabelingTextureData = this._textureMetaElementToMergedIdsMap.getValue(textAreaUsage.identity.metaElementName);
        const previousId: MetaElementUsageIdentityVO = labelingData.previousMetaUsageId;
        const nextId: MetaElementUsageIdentityVO = labelingData.nextMetaUsageId;
        textAreaUsage.previousMergedUsageId = previousId;
        textAreaUsage.nextMergedUsageId = nextId;
        const previousTextureUsage: TextureAreaUsageVO = previousId
          ? (projectMetaElementService.getMetaElementUsage(previousId.articleUsageId, previousId.metaElementName) as TextureAreaUsageVO)
          : null;
        if (previousTextureUsage) {
          previousTextureUsage.nextMergedUsageId = textAreaUsage.identity;
        }
        const nextTextureUsage: TextureAreaUsageVO = nextId
          ? (projectMetaElementService.getMetaElementUsage(nextId.articleUsageId, nextId.metaElementName) as TextureAreaUsageVO)
          : null;
        if (nextTextureUsage) {
          nextTextureUsage.previousMergedUsageId = textAreaUsage.identity;
        }
      }
    }
  }

  private restoreGivingMetaUsages(articleUsage: ArticleUsageVO, projectMetaElementService: ProjectMetaElementService): void {
    if (!articleUsage || !articleUsage.metaElementUsages) {
      return;
    }
    // IMPORTANT: Do not use the backward util methods here!
    // This method creates the necessary backward information.
    let connectionAreaUsages = ArticleUsageUtils.connectionAreaUsages(articleUsage);
    const backwardConnectionAreaUsageCandidates =
      connectionAreaUsages.filter(usage => !usage.identity.equals(usage.connection.acceptingAreaId));
    for (const backwardConnectionAreaUsageCandidate of backwardConnectionAreaUsageCandidates) {
      // this meta element is connected to accepting connection from another article
      const acceptingId: MetaElementUsageIdentityVO = backwardConnectionAreaUsageCandidate.connection.acceptingAreaId;
      const acceptingConnectionAreaUsage = projectMetaElementService.getConnectionAreaUsage(acceptingId.articleUsageId, acceptingId.metaElementName);
      if (acceptingConnectionAreaUsage) {
        this.addGivingUsageIdToConnection(acceptingConnectionAreaUsage.connection, backwardConnectionAreaUsageCandidate.identity);
      }
      this.addGivingUsageIdToConnection(backwardConnectionAreaUsageCandidate.connection, backwardConnectionAreaUsageCandidate.identity);
    }
  }

  private restoreAcceptingMetaUsages(articleUsage: ArticleUsageVO): void {
    if (!articleUsage || !articleUsage.metaElementUsages) {
      return;
    }
    for (const connectionAreaUsage of ArticleUsageUtils.connectionAreaUsages(articleUsage)) {
      if (
        ConnectionAreaUsageUtils.isForwardConnection(connectionAreaUsage) &&
        this._acceptingMetaElementToGivingIdsMap.containsKey(connectionAreaUsage.identity.metaElementName)
      ) {
        // if accepting area usage
        const givingIds: MetaElementUsageIdentityVO[] = this._acceptingMetaElementToGivingIdsMap.getValue(connectionAreaUsage.identity.metaElementName);
        for (const givingId of givingIds) {
          this.addGivingUsageIdToConnection(connectionAreaUsage.connection, givingId);
        }
      }
    }
  }

  private addGivingUsageIdToConnection(connection: ConnectionVO, metaUsageId: MetaElementUsageIdentityVO): void {
    if (!connection.givingAreaIds) {
      connection.givingAreaIds = [];
    }
    let givingIdExists: boolean = false;
    for (const givingId of connection.givingAreaIds) {
      if (metaUsageId.equals(givingId)) {
        givingIdExists = true;
        break;
      }
    }
    if (!givingIdExists) {
      connection.givingAreaIds.push(metaUsageId);
    }
  }

  public equals(oper: any): boolean {
    if (!(oper instanceof BaseArticleOperation)) {
      return false;
    }
    return this._articleUsage.id === oper._articleUsage.id && this._compositionId === oper._compositionId;
  }
}
